import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AttentionTriangleIcon } from '@rsos/assets/icons';
import { gspx } from '@rsos/capstone';
import { APPLE_ADR } from '@rsos/capstone/src/constants/capabilities';
import Loader from '@rsos/components/capstone/Loader/Loader';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import { setAdrQueryStatus } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { APPLE_ADR_PERMISSION } from '../../../constants/permissions';
import { getMostRecentLoc } from '../../../utils/getMostRecentLoc';
import { Container, NoData } from './AdrQueryContainer.styles';

const trackButtonClick = () => {
  gspx.trackCustomEvent('ADR', {
    category: 'ADR',
    name: 'User clicks on ADR Query button',
  });
};

const useSelectedCall = () => {
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const call = useSelector(
    state => state.emergencyCalls.callQueue[selectedCallerID],
  );

  return call;
};

/**
 * Hook to determine if user has the ability to access the Request ADR button
 */
const useHasAccessToViewButton = () => {
  const activeCapabilities = useSelector(
    state => state.psaps?.currentPSAP?.activeCapabilities,
  );
  const permissions = useSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, 'capstone'),
  );

  const allowedCapabilities = [APPLE_ADR];
  const allowedPermissions = [APPLE_ADR_PERMISSION];

  let hasCapabilityAccess = false,
    hasPermissionEnabled = false;
  allowedCapabilities.forEach(c => {
    if (activeCapabilities?.[c]) {
      hasCapabilityAccess = true;
    }
  });

  allowedPermissions.forEach(p => {
    if (permissions.includes(p)) {
      hasPermissionEnabled = true;
    }
  });

  return hasCapabilityAccess && hasPermissionEnabled;
};

const AdrQueryContainer = () => {
  const selectedCall = useSelectedCall();
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const dispatch = useDispatch();

  const adrQueryData = selectedCall?.adrQuery;

  const isFetched = adrQueryData.fetched;
  const isLoading = adrQueryData.loading;
  const hasData = adrQueryData?.hasData;

  const hasAccessToViewButton = useHasAccessToViewButton();

  /**
   * Function to update the loading status for the adr query to trigger a Hermes
   * subscription
   */
  const handleClick = useCallback(() => {
    dispatch(
      setAdrQueryStatus({
        callerID: selectedCallerID,
        loading: true,
        fetched: false,
      }),
    );
    trackButtonClick();
  }, [dispatch, selectedCallerID]);

  const shouldRender = useMemo(() => {
    const mostRecentLoc = getMostRecentLoc(selectedCall);
    return (
      !!hasAccessToViewButton &&
      mostRecentLoc.application.toLowerCase() === 'ios'
    );
  }, [hasAccessToViewButton, selectedCall]);

  if (!shouldRender) {
    return null;
  } else if (!hasData) {
    return (
      <Container>
        <Box width="100%" mb="16px">
          <Button grow onClick={handleClick} disabled={isLoading} noMargin>
            Check for Additional Apple Data
          </Button>
        </Box>
        {isLoading && (
          <Box width="100%" horizontal justify="center">
            <Loader />
          </Box>
        )}
        {isFetched && (
          <NoData horizontal px="12px" py="20px">
            <Box pr="12px" justify="center">
              <AttentionTriangleIcon />
            </Box>
            <Text size="normal">
              Failed to retrieve additional data. Click the above button to try
              again
            </Text>
          </NoData>
        )}
      </Container>
    );
  }
  return null;
};

export default AdrQueryContainer;
